import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useManuscript } from '@/context/manuscript-context';
import { Button } from '@/components/ui/button';
import { Highlighter } from 'lucide-react';
import { RangeHandle } from './range-handle';

/**
 * ManuscriptInteractionLayer
 * 
 * Handles text selection and mark editing:
 * - Shows a floating action button on mobile when selection is stable
 * - Detects taps on existing marks to enter editing mode
 * - Renders draggable handles for adjusting mark boundaries
 */
export function ManuscriptInteractionLayer() {
    const {
        activeSelection,
        setActiveSelection,
        editingMarkId,
        setEditingMarkId,
        setDraggingSide,
        connectionDraft,
        setConnectionDraft,
        marks,
        marksByWord,
        editingConnectionId,
        setEditingConnectionId,
        addMark,
        addConnection,
        updateMark,
        drawModeStyleId,
        showAnnotations,
        wordIndexMap,
        interactionConstraint,
        setActiveTab,
        setDrawModeStyleId
    } = useManuscript();

    const lastValidSelectionRef = useRef<{ startWordId: string; endWordId: string } | null>(null);
    const stableTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const [pendingSelection, setPendingSelection] = useState<{ startWordId: string; endWordId: string } | null>(null);

    // Track original boundary when drag starts (to handle crossing correctly)
    const originalBoundaryRef = useRef<{ wordId: string; wordIdx: number } | null>(null);

    const wordMapRef = useRef(wordIndexMap);
    useEffect(() => {
        wordMapRef.current = wordIndexMap;
    }, [wordIndexMap]);

    const editingMark = useMemo(() =>
        marks.find(m => m.id === editingMarkId),
        [marks, editingMarkId]);

    // Force selection clear and global blockers when editing
    useEffect(() => {
        const preventStandardGestures = (e: Event) => {
            if (editingMarkId) {
                e.preventDefault();
                e.stopPropagation();
            }
        };

        if (editingMarkId) {
            window.getSelection()?.removeAllRanges();
            document.body.style.userSelect = 'none';
            document.body.style.webkitUserSelect = 'none';
            document.addEventListener('selectstart', preventStandardGestures, { capture: true });
            document.addEventListener('dragstart', preventStandardGestures, { capture: true });
        } else {
            document.body.style.userSelect = '';
            document.body.style.webkitUserSelect = '';
        }

        return () => {
            document.body.style.userSelect = '';
            document.body.style.webkitUserSelect = '';
            document.removeEventListener('selectstart', preventStandardGestures, { capture: true });
            document.removeEventListener('dragstart', preventStandardGestures, { capture: true });
        };
    }, [editingMarkId]);

    const handleOpenTools = useCallback(() => {
        const selection = pendingSelection || lastValidSelectionRef.current;
        if (selection) {
            setActiveSelection(selection);
            setActiveTab('highlight');
            setPendingSelection(null);
        }
    }, [pendingSelection, setActiveSelection, setActiveTab]);

    // Called when drag starts to capture the original boundary position
    const handleDragStart = useCallback((side: 'start' | 'end') => {
        setDraggingSide(side);
        const mark = marks.find(m => m.id === editingMarkId);
        if (!mark) return;

        const otherWordId = side === 'start' ? mark.endWordId : mark.startWordId;
        const otherWordIdx = wordIndexMap.get(otherWordId);

        if (otherWordIdx !== undefined) {
            originalBoundaryRef.current = { wordId: otherWordId, wordIdx: otherWordIdx };
        }
    }, [editingMarkId, marks, wordIndexMap, setDraggingSide]);

    const handleDragHandle = useCallback((side: 'start' | 'end', wordId: string) => {
        if (!editingMarkId) return;

        // Simply update the side we are dragging. 
        // We allow startWordId > endWordId during the active drag 
        // to prevent handles from jumping to the same position.
        // The highlight logic in ManuscriptProvider handles min/max indices.
        updateMark(editingMarkId, {
            [side === 'start' ? 'startWordId' : 'endWordId']: wordId
        });
    }, [editingMarkId, updateMark]);

    // Coordinate the end of a drag: normalize the mark then clear refs
    const handleDragComplete = useCallback(() => {
        if (!editingMarkId) return;

        const mark = marks.find(m => m.id === editingMarkId);
        if (!mark) {
            originalBoundaryRef.current = null;
            setDraggingSide(null);
            return;
        }

        const startIdx = wordIndexMap.get(mark.startWordId);
        const endIdx = wordIndexMap.get(mark.endWordId);
        const boundary = originalBoundaryRef.current;

        // Reset side immediately for visual cleanliness
        setDraggingSide(null);

        if (startIdx !== undefined && endIdx !== undefined && boundary && startIdx > endIdx) {
            const findWordByIdx = (targetIdx: number) => {
                for (const [wId, idx] of wordIndexMap.entries()) {
                    if (idx === targetIdx) return wId;
                }
                return null;
            };

            // Case: User dragged 'start' past original 'end'
            // [3, 5] -> drag 3 to 7. Result [7, 5]. Normalize to [6, 7].
            if (startIdx > boundary.wordIdx) {
                const newStartWordId = findWordByIdx(boundary.wordIdx + 1) || boundary.wordId;
                updateMark(editingMarkId, {
                    startWordId: newStartWordId,
                    endWordId: mark.startWordId
                });
            }
            // Case: User dragged 'end' before original 'start'
            // [3, 5] -> drag 5 to 1. Result [3, 1]. Normalize to [1, 2].
            else if (endIdx < boundary.wordIdx) {
                const newEndWordId = findWordByIdx(boundary.wordIdx - 1) || boundary.wordId;
                updateMark(editingMarkId, {
                    startWordId: mark.endWordId,
                    endWordId: newEndWordId
                });
            }
        }

        originalBoundaryRef.current = null;
    }, [editingMarkId, marks, wordIndexMap, updateMark, setDraggingSide]);

    const isWordAllowed = useCallback((wordId: string) => {
        if (!interactionConstraint?.restrictToRange) return true;
        const { startWordId, endWordId } = interactionConstraint.restrictToRange;
        const sIdx = wordIndexMap.get(startWordId);
        const eIdx = wordIndexMap.get(endWordId);
        const cIdx = wordIndexMap.get(wordId);
        if (sIdx === undefined || eIdx === undefined || cIdx === undefined) return false;
        const min = Math.min(sIdx, eIdx);
        const max = Math.max(sIdx, eIdx);
        return cIdx >= min && cIdx <= max;
    }, [interactionConstraint, wordIndexMap]);

    useEffect(() => {
        const findWordIdFromNode = (node: Node | null, offset: number): string | null => {
            if (!node) return null;

            // Case 1: Node is a word span or inside one (most common)
            let current: HTMLElement | null = node.nodeType === Node.ELEMENT_NODE ? (node as HTMLElement) : (node.parentElement);
            while (current && current !== document.body) {
                if (current.id?.startsWith('w-')) return current.id;
                current = current.parentElement;
            }

            // Case 2: Node is a container (div/paragraph) and offset points to a child node
            if (node.nodeType === Node.ELEMENT_NODE) {
                const element = node as HTMLElement;
                const child = element.childNodes[offset] || element.childNodes[offset - 1];
                if (child && child.nodeType === Node.ELEMENT_NODE) {
                    const childEl = child as HTMLElement;
                    if (childEl.id?.startsWith('w-')) return childEl.id;
                    const nested = childEl.querySelector('[id^="w-"]');
                    if (nested) return nested.id;
                }
            }

            return null;
        };

        const showActionBubble = () => {
            if (drawModeStyleId) return;
            const captured = lastValidSelectionRef.current;
            if (!captured) return;

            const map = wordMapRef.current;
            const startIdx = map.get(captured.startWordId);
            const endIdx = map.get(captured.endWordId);

            if (startIdx !== undefined && endIdx !== undefined) {
                const finalRange = startIdx <= endIdx ? captured : { startWordId: captured.endWordId, endWordId: captured.startWordId };
                setPendingSelection(finalRange);
            }
        };

        const handleMouseDown = (e: MouseEvent) => {
            // ALT + Drag to start a connection
            if (e.altKey) {
                if (interactionConstraint?.onlyAllow && !interactionConstraint.onlyAllow.includes('connect')) return;
                const target = e.target as HTMLElement;
                const wordSpan = target.closest('span[id^="w-"]');
                if (wordSpan && isWordAllowed(wordSpan.id)) {
                    setConnectionDraft({
                        startWordId: wordSpan.id,
                        currentPos: { x: e.clientX, y: e.clientY }
                    });
                    e.preventDefault();
                    e.stopPropagation();
                }
            }
        };

        const handleMouseMove = (e: MouseEvent) => {
            if (connectionDraft) {
                setConnectionDraft({
                    ...connectionDraft,
                    currentPos: { x: e.clientX, y: e.clientY }
                });
            }
        };

        const handleMouseUpGlobal = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            if (target.closest('[data-manuscript-tools]') ||
                target.closest('.manuscript-action-bubble') ||
                target.closest('[data-radix-portal]')) {
                if (connectionDraft) setConnectionDraft(null);
                return;
            }

            // --- DRAW MODE AUTO-APPLY ---
            if (drawModeStyleId && !e.altKey && !connectionDraft && !editingMarkId) {
                setTimeout(() => {
                    const captured = lastValidSelectionRef.current;
                    const sel = window.getSelection();

                    // Only apply if there was a selection (not just a click)
                    if (!captured || !sel || sel.isCollapsed) return;

                    // Apply the mark automatically
                    addMark({
                        markStyleId: drawModeStyleId,
                        startWordId: captured.startWordId,
                        endWordId: captured.endWordId,
                    });

                    // Update UI selection to the new mark so "Active Elements" updates
                    setActiveSelection(captured);

                    // Clear selection and refs
                    window.getSelection()?.removeAllRanges();
                    lastValidSelectionRef.current = null;
                    setPendingSelection(null);
                }, 50); // Small delay to ensure selection is final
                return;
            }

            if (connectionDraft) {
                const element = document.elementFromPoint(e.clientX, e.clientY);
                const wordSpan = element?.closest('span[id^="w-"]');

                if (wordSpan && wordSpan.id !== connectionDraft.startWordId) {
                    if (isWordAllowed(wordSpan.id)) {
                        addConnection({
                            startWordId: connectionDraft.startWordId,
                            endWordId: wordSpan.id,
                            connectionStyleId: 'default-arrow',
                            label: ''
                        });
                    }
                }

                setConnectionDraft(null);
            }

            // Existing logic for normal selection
            if (editingMarkId || activeSelection) return;

            // Simple auto-open logic: if we have a captured selection, open the sheet
            if (drawModeStyleId === null && !e.altKey && !connectionDraft) {
                setTimeout(() => {
                    const captured = lastValidSelectionRef.current;
                    // Check activeSelection again inside timeout to catch race conditions
                    if (!captured || activeSelection) return;

                    const map = wordMapRef.current;
                    const sIdx = map.get(captured.startWordId);
                    const eIdx = map.get(captured.endWordId);

                    if (sIdx !== undefined && eIdx !== undefined) {
                        const finalRange = sIdx <= eIdx ? captured : { startWordId: captured.endWordId, endWordId: captured.startWordId };
                        setActiveSelection(finalRange);
                        setActiveTab('highlight');
                        setPendingSelection(null);
                    }
                }, 100);
            }
        };

        const handleSelectionChange = () => {
            if (connectionDraft) return;
            const sel = window.getSelection();
            if (sel && !sel.isCollapsed && sel.rangeCount > 0) {
                const sId = findWordIdFromNode(sel.anchorNode, sel.anchorOffset);
                const eId = findWordIdFromNode(sel.focusNode, sel.focusOffset);

                if (sId && eId) {
                    try {
                        const sWordAllowed = isWordAllowed(sId);
                        const eWordAllowed = isWordAllowed(eId);

                        if (!sWordAllowed || !eWordAllowed) {
                            sel.removeAllRanges();
                            return;
                        }

                        if (interactionConstraint?.onlyAllow) {
                            const allowed = interactionConstraint.onlyAllow;
                            const canSelect = allowed.includes('highlight') || allowed.includes('mark') || allowed.includes('structure') || allowed.includes('note');
                            if (!canSelect) {
                                sel.removeAllRanges();
                                return;
                            }
                        }

                        const map = wordMapRef.current;
                        const sIdx = map.get(sId);
                        const eIdx = map.get(eId);

                        if (sIdx !== undefined && eIdx !== undefined) {
                            const standardized = sIdx <= eIdx
                                ? { startWordId: sId, endWordId: eId }
                                : { startWordId: eId, endWordId: sId };

                            lastValidSelectionRef.current = standardized;
                            if (stableTimeoutRef.current) clearTimeout(stableTimeoutRef.current);
                            stableTimeoutRef.current = setTimeout(showActionBubble, 400);
                        }
                    } catch (err) {
                        // Silent fail
                    }
                }
            } else {
                setPendingSelection(null);
            }
        };

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                setActiveSelection(null);
                setEditingMarkId(null);
                setEditingConnectionId(null);
                setDrawModeStyleId(null);
                window.getSelection()?.removeAllRanges();
            }
        };

        const handleClickCheck = (e: MouseEvent) => {
            if (e.altKey) return; // Ignore for alt-drags

            const target = e.target as HTMLElement;

            // Be extremely defensive: check if target or any parent is our tools
            // Even if unmounted, target.closest should still work on the detached fragment
            if (target.closest('[data-manuscript-tools]') ||
                target.closest('.manuscript-action-bubble') ||
                target.closest('[data-radix-portal]')) return;

            if (editingMarkId || editingConnectionId) {
                if (target.closest('.range-handle') || target.tagName === 'BUTTON' || target.closest('svg')) return;
            }

            setTimeout(() => {
                const sel = window.getSelection();
                if (!sel || sel.isCollapsed) {
                    const target = e.target as HTMLElement;
                    const wordSpan = target.closest('span[id^="w-"]');
                    if (wordSpan) {
                        const wordId = wordSpan.id;
                        if (!isWordAllowed(wordId)) return;

                        const wordMarks = marksByWord.get(wordId);
                        if (wordMarks && wordMarks.length > 0 && !drawModeStyleId) {
                            const canEdit = !interactionConstraint?.onlyAllow ||
                                interactionConstraint.onlyAllow.includes('highlight') ||
                                interactionConstraint.onlyAllow.includes('mark');
                            if (canEdit) {
                                // Sort wordMarks: Prefer smaller ranges first (more specific), then by dateUpdated (freshest)
                                const sorted = [...wordMarks].sort((a, b) => {
                                    const aStart = wordIndexMap.get(a.startWordId) || 0;
                                    const aEnd = wordIndexMap.get(a.endWordId) || 0;
                                    const bStart = wordIndexMap.get(b.startWordId) || 0;
                                    const bEnd = wordIndexMap.get(b.endWordId) || 0;
                                    const aSize = Math.abs(aEnd - aStart);
                                    const bSize = Math.abs(bEnd - bStart);

                                    if (aSize !== bSize) return aSize - bSize;
                                    return (b.dateUpdated || 0) - (a.dateUpdated || 0);
                                });
                                setEditingMarkId(sorted[0].id);
                                return;
                            }
                        }
                    }

                    setPendingSelection(null);
                    setEditingMarkId(null);
                    lastValidSelectionRef.current = null;
                }
            }, 100);
        };

        document.addEventListener('mousedown', handleMouseDown, { capture: true });
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUpGlobal);
        document.addEventListener('selectionchange', handleSelectionChange);
        document.addEventListener('click', handleClickCheck);
        document.addEventListener('touchend', handleClickCheck as any, { passive: true });
        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('mousedown', handleMouseDown, { capture: true });
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUpGlobal);
            document.removeEventListener('selectionchange', handleSelectionChange);
            document.removeEventListener('click', handleClickCheck);
            document.removeEventListener('touchend', handleClickCheck as any);
            document.removeEventListener('keydown', handleKeyDown);
            if (stableTimeoutRef.current) clearTimeout(stableTimeoutRef.current);
        };
    }, [activeSelection, drawModeStyleId, marksByWord, setActiveSelection, setEditingMarkId, editingMarkId, editingConnectionId, setEditingConnectionId, connectionDraft, setConnectionDraft, addConnection, interactionConstraint, wordIndexMap, isWordAllowed]);

    if (!showAnnotations) return null;

    return (
        <>
            {/* Global hard-kill for browser selection when editing */}
            {editingMarkId && (
                <style dangerouslySetInnerHTML={{
                    __html: `
                    * { 
                        user-select: none !important; 
                        -webkit-user-select: none !important; 
                        -webkit-touch-callout: none !important;
                    }
                    .range-handle {
                        pointer-events: auto !important;
                        user-select: none !important;
                    }
                `}} />
            )}

            {/* Draggable handles for editing existing marks */}
            {editingMark && (
                <>
                    <RangeHandle
                        key={`${editingMark.id}-start`}
                        wordId={editingMark.startWordId}
                        type="start"
                        onDragStart={() => handleDragStart('start')}
                        onDrag={(wordId) => handleDragHandle('start', wordId)}
                        onDragComplete={handleDragComplete}
                    />
                    <RangeHandle
                        key={`${editingMark.id}-end`}
                        wordId={editingMark.endWordId}
                        type="end"
                        onDragStart={() => handleDragStart('end')}
                        onDrag={(wordId) => handleDragHandle('end', wordId)}
                        onDragComplete={handleDragComplete}
                    />
                </>
            )}

            {/* Mobile Action Bubble */}
            {pendingSelection && !activeSelection && !editingMarkId && (
                <Button
                    onClick={handleOpenTools}
                    size="icon"
                    className="manuscript-action-bubble fixed bottom-32 right-4 z-[9998] rounded-full h-14 w-14 shadow-lg animate-in fade-in zoom-in duration-200"
                    aria-label="Open marking tools"
                >
                    <Highlighter className="h-6 w-6" />
                </Button>
            )}
        </>
    );
}
